import React, { useEffect, useState } from "react";
import "./CustomerProfile.css";
import { backend_url } from "../../App";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CATEGORIES = ["Retail", "Boutique", "Reseller", "VIP"];

const MASTER_FIELDS = [
  { key: "name", label: "Name", type: "text" },
  { key: "email", label: "Email ID", type: "email" },
  { key: "phoneNumber", label: "Mobile Number", type: "text" },
  { key: "whatsappNumber", label: "WhatsApp Number", type: "text" },
  { key: "address", label: "Address", type: "text" },
  { key: "city", label: "City", type: "text" },
  { key: "state", label: "State", type: "text" },
  { key: "country", label: "Country", type: "text" },
  { key: "pincode", label: "Pincode", type: "text" },
  { key: "dob", label: "Date of Birth", type: "date" },
  { key: "anniversary", label: "Anniversary", type: "date" },
  { key: "referralSource", label: "Referred By", type: "text" },
];

const TAG_FIELDS = [
  {
    key: "favouriteColours", label: "Favourite Colours",
    options: ["Red", "Pink", "Blue", "Green", "Yellow", "Purple", "Maroon", "Black", "White", "Gold", "Silver", "Orange", "Beige"],
  },
  {
    key: "favouritePrints", label: "Favourite Prints",
    options: ["Floral", "Geometric", "Polka Dot", "Paisley", "Abstract", "Animal Print", "Checks", "Stripes", "Ikat", "Batik"],
  },
  {
    key: "preferredNeckPatterns", label: "Preferred Neck Patterns",
    options: ["Round Neck", "V-Neck", "Boat Neck", "Halter Neck", "Sweetheart Neck", "Collar Neck", "High Neck", "Square Neck"],
  },
  {
    key: "sleevePreferences", label: "Sleeve Preferences",
    options: ["Sleeveless", "Short Sleeve", "3/4 Sleeve", "Full Sleeve", "Bell Sleeve", "Puff Sleeve", "Cap Sleeve"],
  },
  {
    key: "preferredFabrics", label: "Preferred Fabrics",
    options: ["Cotton", "Silk", "Linen", "Georgette", "Chiffon", "Crepe", "Rayon", "Velvet", "Organza", "Net"],
  },
  {
    key: "preferredEmbellishments", label: "Preferred Embellishments",
    options: ["Aari Work", "Kundan Work", "Zardosi Work", "Hand Embroidery", "Stone Work", "Mirror Work", "Bead Work"],
  },
];

const MEASUREMENT_FIELDS = [
  { key: "height", label: "Height" },
  { key: "bust", label: "Bust" },
  { key: "upperBust", label: "Upper Bust" },
  { key: "waist", label: "Waist" },
  { key: "hip", label: "Hip" },
  { key: "shoulder", label: "Shoulder" },
  { key: "armRound", label: "Arm Round" },
  { key: "sleeveLength", label: "Sleeve Length" },
  { key: "frontNeckDepth", label: "Front Neck Depth" },
  { key: "backNeckDepth", label: "Back Neck Depth" },
  { key: "kurtiLength", label: "Kurti Length" },
  { key: "blouseLength", label: "Blouse Length" },
  { key: "salwarLength", label: "Salwar Length" },
  { key: "inskirtLength", label: "Inskirt Length" },
];

const toDateInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "");

const emptyMasterForm = () =>
  MASTER_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), { customerCategory: "Retail" });

const emptyMeasurementForm = () =>
  MEASUREMENT_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), { unit: "inches" });

// customerId === null means "register a new customer" mode.
const CustomerProfile = ({ customerId, onClose, onSaved }) => {
  const isNew = !customerId;
  const [activeTab, setActiveTab] = useState("master");
  const [loading, setLoading] = useState(!isNew);
  const [record, setRecord] = useState(null);
  const [masterForm, setMasterForm] = useState(emptyMasterForm());
  const [profileForm, setProfileForm] = useState(
    TAG_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: [], [`${f.key}Other`]: "" }), { personalStylePreference: "" }),
  );
  const [measurements, setMeasurements] = useState([]);
  const [measurementForm, setMeasurementForm] = useState(emptyMeasurementForm());
  const [trialNoteDrafts, setTrialNoteDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backend_url}/api/customers/${customerId}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message);

      setRecord(data.customer);
      const m = emptyMasterForm();
      MASTER_FIELDS.forEach((f) => {
        const val = data.customer[f.key];
        m[f.key] = f.type === "date" ? toDateInput(val) : val || "";
      });
      m.customerCategory = data.customer.customerCategory || "Retail";
      setMasterForm(m);

      const p = { personalStylePreference: data.profile?.personalStylePreference || "" };
      TAG_FIELDS.forEach((f) => {
        const values = data.profile?.[f.key] || [];
        p[f.key] = values.filter((v) => f.options.includes(v));
        p[`${f.key}Other`] = values.filter((v) => !f.options.includes(v)).join(", ");
      });
      setProfileForm(p);

      setMeasurements(data.measurements || []);
    } catch (err) {
      toast.error("Failed to load customer record");
    } finally {
      setLoading(false);
    }
  };

  const handleMasterChange = (key, value) => setMasterForm((p) => ({ ...p, [key]: value }));
  const handleProfileChange = (key, value) => setProfileForm((p) => ({ ...p, [key]: value }));
  const handleMeasurementChange = (key, value) => setMeasurementForm((p) => ({ ...p, [key]: value }));

  const toggleTagOption = (fieldKey, option) => {
    setProfileForm((prev) => {
      const current = prev[fieldKey] || [];
      const next = current.includes(option) ? current.filter((v) => v !== option) : [...current, option];
      return { ...prev, [fieldKey]: next };
    });
  };

  const saveMaster = async () => {
    if (!masterForm.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await fetch(`${backend_url}/api/customers`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(masterForm),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message);
        toast.success("Customer registered");
        onSaved && onSaved(data.customer);
        onClose();
      } else {
        const res = await fetch(`${backend_url}/api/customers/${customerId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(masterForm),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message);
        toast.success("Customer details updated");
        setRecord(data.customer);
        onSaved && onSaved(data.customer);
      }
    } catch (err) {
      toast.error("Failed to save customer details");
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = { personalStylePreference: profileForm.personalStylePreference };
      TAG_FIELDS.forEach((f) => {
        const other = (profileForm[`${f.key}Other`] || "").split(",").map((v) => v.trim()).filter(Boolean);
        payload[f.key] = [...(profileForm[f.key] || []), ...other];
      });
      const res = await fetch(`${backend_url}/api/customers/${customerId}/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      toast.success("Style preferences saved");
    } catch (err) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const addMeasurement = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${backend_url}/api/customers/${customerId}/measurements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(measurementForm),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      toast.success("Measurement revision added");
      setMeasurements((prev) => [data.measurement, ...prev]);
      setMeasurementForm(emptyMeasurementForm());
    } catch (err) {
      toast.error("Failed to add measurement");
    } finally {
      setSaving(false);
    }
  };

  const addTrialNote = async (measurementId) => {
    const note = (trialNoteDrafts[measurementId] || "").trim();
    if (!note) return;
    try {
      const res = await fetch(
        `${backend_url}/api/customers/${customerId}/measurements/${measurementId}/trial-notes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ note }),
        },
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setMeasurements((prev) => prev.map((m) => (m._id === measurementId ? data.measurement : m)));
      setTrialNoteDrafts((prev) => ({ ...prev, [measurementId]: "" }));
    } catch (err) {
      toast.error("Failed to add trial note");
    }
  };

  return (
    <>
      <div className="cp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="cp-modal">
          <button className="cp-close" onClick={onClose}>✕</button>

          <div className="cp-header">
            <h2>{isNew ? "Register New Customer" : masterForm.name || "Customer"}</h2>
            {record?.customerId && <span className="cp-id-chip">{record.customerId}</span>}
          </div>

          {!isNew && (
            <div className="cp-tabs">
              <button className={`cp-tab ${activeTab === "master" ? "active" : ""}`} onClick={() => setActiveTab("master")}>Master Info</button>
              <button className={`cp-tab ${activeTab === "preferences" ? "active" : ""}`} onClick={() => setActiveTab("preferences")}>Style Preferences</button>
              <button className={`cp-tab ${activeTab === "measurements" ? "active" : ""}`} onClick={() => setActiveTab("measurements")}>
                Measurements {measurements.length > 0 && <span className="cp-tab-badge">{measurements.length}</span>}
              </button>
            </div>
          )}

          <div className="cp-body">
            {loading ? (
              <p className="cp-loading">Loading…</p>
            ) : (
              <>
                {(isNew || activeTab === "master") && (
                  <div className="cp-form-grid">
                    {MASTER_FIELDS.map((f) => (
                      <label key={f.key} className="cp-field">
                        <span>{f.label}</span>
                        <input
                          type={f.type}
                          value={masterForm[f.key] || ""}
                          onChange={(e) => handleMasterChange(f.key, e.target.value)}
                        />
                      </label>
                    ))}
                    <label className="cp-field">
                      <span>Customer Category</span>
                      <select
                        value={masterForm.customerCategory}
                        onChange={(e) => handleMasterChange("customerCategory", e.target.value)}
                      >
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                    <div className="cp-actions">
                      <button className="cp-save-btn" disabled={saving} onClick={saveMaster}>
                        {saving ? "Saving…" : isNew ? "Register Customer" : "Save Changes"}
                      </button>
                    </div>
                  </div>
                )}

                {!isNew && activeTab === "preferences" && (
                  <div className="cp-form-grid">
                    <label className="cp-field cp-field-wide">
                      <span>Personal Style Preference</span>
                      <textarea
                        rows={2}
                        value={profileForm.personalStylePreference}
                        onChange={(e) => handleProfileChange("personalStylePreference", e.target.value)}
                      />
                    </label>
                    {TAG_FIELDS.map((f) => (
                      <div key={f.key} className="cp-field cp-field-wide">
                        <span>{f.label}</span>
                        <div className="cp-chip-grid">
                          {f.options.map((opt) => (
                            <label key={opt} className="cp-chip">
                              <input
                                type="checkbox"
                                checked={(profileForm[f.key] || []).includes(opt)}
                                onChange={() => toggleTagOption(f.key, opt)}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                        <input
                          type="text"
                          className="cp-chip-other"
                          placeholder="Other (comma-separated)"
                          value={profileForm[`${f.key}Other`] || ""}
                          onChange={(e) => handleProfileChange(`${f.key}Other`, e.target.value)}
                        />
                      </div>
                    ))}
                    <div className="cp-actions">
                      <button className="cp-save-btn" disabled={saving} onClick={saveProfile}>
                        {saving ? "Saving…" : "Save Preferences"}
                      </button>
                    </div>
                  </div>
                )}

                {!isNew && activeTab === "measurements" && (
                  <div className="cp-measurements">
                    <div className="cp-measure-new">
                      <h4>Add New Measurement Revision</h4>
                      <div className="cp-form-grid">
                        {MEASUREMENT_FIELDS.map((f) => (
                          <label key={f.key} className="cp-field">
                            <span>{f.label}</span>
                            <input
                              type="number"
                              step="0.1"
                              value={measurementForm[f.key]}
                              onChange={(e) => handleMeasurementChange(f.key, e.target.value)}
                            />
                          </label>
                        ))}
                        <label className="cp-field">
                          <span>Unit</span>
                          <select
                            value={measurementForm.unit}
                            onChange={(e) => handleMeasurementChange("unit", e.target.value)}
                          >
                            <option value="inches">inches</option>
                            <option value="cm">cm</option>
                          </select>
                        </label>
                      </div>
                      <div className="cp-actions">
                        <button className="cp-save-btn" disabled={saving} onClick={addMeasurement}>
                          {saving ? "Saving…" : "Add Revision"}
                        </button>
                      </div>
                    </div>

                    <h4 className="cp-history-title">Revision History</h4>
                    {measurements.length === 0 ? (
                      <p className="cp-no-data">No measurements recorded yet.</p>
                    ) : (
                      <div className="cp-history-list">
                        {measurements.map((m) => (
                          <div key={m._id} className="cp-history-card">
                            <div className="cp-history-top">
                              <span className="cp-history-date">
                                {new Date(m.recordedAt).toLocaleDateString()}
                              </span>
                              <span className="cp-history-unit">{m.unit}</span>
                            </div>
                            <div className="cp-history-grid">
                              {MEASUREMENT_FIELDS.filter((f) => m[f.key] !== undefined && m[f.key] !== null).map((f) => (
                                <div key={f.key} className="cp-history-item">
                                  <span>{f.label}</span>
                                  <strong>{m[f.key]}</strong>
                                </div>
                              ))}
                            </div>
                            {m.trialNotes?.length > 0 && (
                              <div className="cp-trial-notes">
                                {m.trialNotes.map((t, i) => (
                                  <div key={i} className="cp-trial-note">
                                    <span>{new Date(t.createdAt).toLocaleDateString()}</span> — {t.note}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="cp-trial-add">
                              <input
                                type="text"
                                placeholder="Add trial note…"
                                value={trialNoteDrafts[m._id] || ""}
                                onChange={(e) => setTrialNoteDrafts((p) => ({ ...p, [m._id]: e.target.value }))}
                                onKeyDown={(e) => e.key === "Enter" && addTrialNote(m._id)}
                              />
                              <button onClick={() => addTrialNote(m._id)}>Add</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={2500} />
    </>
  );
};

export default CustomerProfile;
